import { MenuOptionType, getMomentTz } from "@dexteel/mesf-core";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { Box, Button, Tooltip } from "@mui/material";
import type {
	ColDef,
	ICellRendererParams,
	ValueFormatterParams,
	ValueGetterParams,
} from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef } from "react";

import type { WorkOrder } from "@/shared/models/WorkOrder";
import { withFraction32 } from "@/utils/fraction";

const moment = getMomentTz();
import { NotesCell } from "../components/NotesCell";
import { StatusCell } from "../components/StatusCell";
import {
	ASSET_TYPE_CONFIGS,
	type AreaIds,
	type AssetType,
	getTableProductFields,
	resolveAssetType,
} from "../config/assetFieldConfig";

type Props = {
	assetId: number;
	parentAssetId: number | null;
	areaIds: AreaIds;
	isCurrentShift: boolean;
	isPeriod: boolean;
	setSelectedRowData: (wo: WorkOrder) => void;
	setShowModal: (modal: "create" | "update" | "delete" | "times" | "") => void;
	showContextMenu: Function;
};

const leadingColumns: ColDef[] = [
	{
		field: "Sequence",
		headerName: "Priority",
		minWidth: 69,
		flex: 1,
		sortable: false,
		filter: false,
		cellRenderer: (params: ICellRendererParams) => {
			const canDrag = params.data?.Status === "Pending";
			const ref = (el: HTMLElement | null) => {
				if (el && canDrag) params.registerRowDragger(el);
			};
			return (
				<Box
					ref={ref}
					sx={{
						display: "flex",
						alignItems: "center",
						height: "100%",
						cursor: canDrag ? "grab" : "default",
						opacity: canDrag ? 1 : 0.45,
					}}
				>
					<DragIndicatorIcon
						sx={{ fontSize: 18, color: "action.active", mr: 0.5 }}
					/>
					{params.value}
				</Box>
			);
		},
	},
	{
		field: "WorkOrderNumber",
		headerName: "WO #",
		flex: 1.3,
		minWidth: 165,
		sortable: false,
		filter: false,
	},
];

const productIdentityColumns: ColDef[] = [
	{
		field: "PartNumber",
		headerName: "Part Number",
		flex: 2,
		minWidth: 150,
		wrapHeaderText: true,
		autoHeaderHeight: true,
	},
	{
		field: "CustomerName",
		headerName: "Customer",
		flex: 1,
		minWidth: 100,
	},
	{
		field: "SectionName",
		headerName: "Section",
		flex: 1,
		minWidth: 70,
		wrapText: true,
		autoHeight: true,
	},
	{
		field: "GradeName",
		headerName: "Grade",
		flex: 1,
		minWidth: 66,
		wrapText: true,
		autoHeight: true,
	},
];

const clipIdentityColumns: ColDef[] = [
	{
		field: "ClipPartNumber",
		headerName: "Clip Part #",
		flex: 1.4,
		minWidth: 120,
		wrapHeaderText: true,
	},
	{
		colId: "ClipSize",
		headerName: "Size",
		flex: 0.9,
		minWidth: 90,
		valueGetter: (params: ValueGetterParams) => {
			const wo = params.data as WorkOrder;
			return wo?.ClipWidth != null || wo?.ClipHeight != null
				? `${wo.ClipWidth} x ${wo.ClipHeight}`
				: "";
		},
	},
	{
		field: "HolePattern",
		headerName: "Hole Pattern",
		flex: 1.1,
		minWidth: 110,
		wrapHeaderText: true,
	},
	{
		field: "HoleGage",
		headerName: "Hole Gage",
		flex: 0.9,
		minWidth: 90,
		wrapHeaderText: true,
	},
	{
		field: "ClipHoleSizes",
		headerName: "Hole Sizes",
		flex: 1.2,
		minWidth: 110,
		wrapHeaderText: true,
	},
];

const buildAssetSpecificColumns = (assetType: AssetType): ColDef[] => {
	const tableFields = getTableProductFields(assetType);
	return tableFields.map(({ field, label, format, fraction }) => {
		if (format) {
			return {
				colId: field,
				headerName: label,
				flex: 1,
				minWidth: 100,
				valueGetter: (params: ValueGetterParams) => {
					if (!params.data) return "";
					return format(params.data);
				},
			};
		}
		return {
			field,
			headerName: label,
			flex: 1,
			minWidth: fraction ? 145 : 80,
			wrapHeaderText: true,
			...(fraction && {
				valueFormatter: (params: ValueFormatterParams) =>
					withFraction32(params.value),
			}),
		};
	});
};

const buildQuantityColumns = (assetType: AssetType): ColDef[] => {
	const config = ASSET_TYPE_CONFIGS[assetType];
	const quantityColumn: ColDef = {
		field: "Quantity",
		headerName: "Pieces",
		flex: 1,
		minWidth: 80,
		cellStyle: { textAlign: "right" },
	};
	if (config.usesClip) return [quantityColumn];
	return [
		quantityColumn,
		{
			headerName: "Total Bundles",
			flex: 1,
			minWidth: 74,
			wrapHeaderText: true,
			autoHeaderHeight: true,
			cellStyle: { textAlign: "right" },
			valueGetter: (params: ValueGetterParams) => {
				const qty = params.data?.Quantity;
				const bundleCount = params.data?.[config.bundleCountField];
				if (qty && bundleCount && bundleCount > 0) {
					return Math.ceil(qty / bundleCount);
				}
				return null;
			},
		},
		{
			field: config.bundleCountField as string,
			headerName: config.bundleCountLabel,
			flex: 1,
			minWidth: 100,
		},
	];
};

const formatShiftLabel = (
	date: string | null,
	name: string | null,
	crew: string | null,
): string =>
	date
		? `${moment(date).format("MM/DD/YYYY")} ${name ?? ""}-${crew ?? ""}`
		: "";

// Live board: one operative-shift column + divergence badge (planned vs actual).
// Historical view: planned (target) and actual (operative) side by side.
// Period view: a single "Shift" column (the range spans many shifts).
const buildShiftColumns = (
	isCurrentShift: boolean,
	isPeriod = false,
): ColDef[] => {
	const operativeCol: ColDef = {
		colId: "Shift",
		headerName: isCurrentShift || isPeriod ? "Shift" : "Actual Shift",
		flex: 1,
		minWidth: 130,
		wrapHeaderText: true,
		autoHeaderHeight: true,
		cellRenderer: (params: ICellRendererParams) => {
			const wo = params.data as WorkOrder;
			const label = formatShiftLabel(
				wo?.ProductionDate,
				wo?.ShiftName,
				wo?.CrewName,
			);
			if (!label) return "";
			const diverged =
				wo.TargetShiftId != null &&
				wo.ShiftId != null &&
				wo.TargetShiftId !== wo.ShiftId;
			if (!diverged) return label;
			const targetLabel = formatShiftLabel(
				wo.TargetProductionDate,
				wo.TargetShiftName,
				wo.TargetCrewName,
			);
			const targetEnd = wo.TargetShiftEnd ? moment(wo.TargetShiftEnd) : null;
			const isLate = !!targetEnd && targetEnd.isBefore(moment());
			const color = isLate ? "error.main" : "info.main";
			const tip = isLate
				? `Planned for ${targetLabel} — carried forward`
				: `Pulled forward — planned for ${targetLabel}`;
			return (
				<Tooltip title={tip} arrow>
					<Box sx={{ color, fontWeight: 600 }}>{label}</Box>
				</Tooltip>
			);
		},
	};
	if (isCurrentShift || isPeriod) return [operativeCol];
	const plannedCol: ColDef = {
		colId: "PlannedShift",
		headerName: "Planned Shift",
		flex: 1.3,
		minWidth: 140,
		wrapHeaderText: true,
		autoHeaderHeight: true,
		cellRenderer: (params: ICellRendererParams) => {
			const wo = params.data as WorkOrder;
			return formatShiftLabel(
				wo?.TargetProductionDate,
				wo?.TargetShiftName,
				wo?.TargetCrewName,
			);
		},
	};
	return [plannedCol, operativeCol];
};

const statusColumn: ColDef = {
	field: "Status",
	headerName: "Status",
	flex: 1,
	minWidth: 110,
	cellRenderer: StatusCell,
};

// Rendered after the shift columns
const trailingColumns: ColDef[] = [
	{
		field: "StartedAt",
		headerName: "Started",
		flex: 1,
		minWidth: 120,
		valueFormatter: (params) =>
			params.value ? moment(params.value).format("MM/DD - HH:mm") : "",
	},
	{
		field: "CompletedAt",
		headerName: "Completed",
		flex: 1,
		minWidth: 120,
		valueFormatter: (params) =>
			params.value ? moment(params.value).format("MM/DD - HH:mm") : "",
	},
	{
		field: "CreatedAt",
		headerName: "Created",
		flex: 1,
		minWidth: 147,
		// CreatedAt is a UTC instant (parsed in the repository); render it in the
		// plant/server timezone, not the browser's.
		valueFormatter: (params) =>
			params.value ? moment(params.value).format("MM/DD/YYYY - HH:mm") : "",
	},
	{
		field: "Notes",
		headerName: "",
		cellRenderer: NotesCell,
		flex: 1,
		minWidth: 50,
		maxWidth: 50,
		filter: false,
		sortable: false,
	},
];

export const useTableData = ({
	assetId,
	parentAssetId,
	areaIds,
	isCurrentShift,
	isPeriod,
	setSelectedRowData,
	setShowModal,
	showContextMenu,
}: Props) => {
	// Refs to avoid stale closures in AG Grid cellRenderer
	const setSelectedRowDataRef = useRef(setSelectedRowData);
	const setShowModalRef = useRef(setShowModal);
	const showContextMenuRef = useRef(showContextMenu);

	useEffect(() => {
		setSelectedRowDataRef.current = setSelectedRowData;
	}, [setSelectedRowData]);

	useEffect(() => {
		setShowModalRef.current = setShowModal;
	}, [setShowModal]);

	useEffect(() => {
		showContextMenuRef.current = showContextMenu;
	}, [showContextMenu]);

	const assetType = resolveAssetType(assetId, parentAssetId, areaIds);

	const actionColumnDef: ColDef = {
		colId: "actions",
		headerName: "",
		sortable: false,
		filter: false,
		maxWidth: 130,
		minWidth: 130,
		valueGetter: (params) => params.data?.Status,
		cellRenderer: (params: ICellRendererParams) => {
			const wo = params.data as WorkOrder;
			return (
				<Box
					sx={{
						height: "100%",
						display: "flex",
						justifyContent: "space-around",
						alignItems: "center",
					}}
				>
					<Box sx={{ display: "flex", gap: 0 }}>
						<Button
							onClick={() => {
								setSelectedRowDataRef.current(wo);
								setShowModalRef.current("update");
							}}
							title="Edit"
							size="small"
							sx={{ minWidth: 28, padding: "4px" }}
						>
							<EditIcon color="primary" />
						</Button>

						<Button
							onClick={() => {
								setSelectedRowDataRef.current(wo);
								setShowModalRef.current("delete");
							}}
							title="Delete"
							size="small"
							disabled={wo.Status !== "Pending"}
							sx={{ minWidth: 28, padding: "4px", color: "disabled" }}
						>
							<DeleteIcon
								color={wo.Status === "Pending" ? "secondary" : "disabled"}
							/>
						</Button>

						<Button
							onClick={(e) =>
								showContextMenuRef.current(
									e.nativeEvent,
									wo,
									"workOrdersContext",
								)
							}
							title="Options"
							size="small"
							sx={{ minWidth: 28, padding: "4px" }}
						>
							<FormatListBulletedSharpIcon color="action" />
						</Button>
					</Box>
				</Box>
			);
		},
	};

	// Period hides the per-shift drag-to-reorder Priority column.
	const columnDefs = useMemo(() => {
		return [
			...(isPeriod ? leadingColumns.slice(1) : leadingColumns),
			...(ASSET_TYPE_CONFIGS[assetType].usesClip
				? clipIdentityColumns
				: productIdentityColumns),
			...buildAssetSpecificColumns(assetType),
			...buildQuantityColumns(assetType),
			statusColumn,
			...buildShiftColumns(isCurrentShift, isPeriod),
			...trailingColumns,
			actionColumnDef,
		];
	}, [assetType, isCurrentShift, isPeriod]);

	// setShowModal and setSelectedRowData are stable React state setters,
	// safe to use directly without adding to deps (avoids menu re-creation on every render).
	// biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
	const getMenuOptions = useCallback((data: WorkOrder) => {
		const options: MenuOptionType[] = [
			{
				name: "New Work Order",
				key: "new_work_order",
				onClick: () => setShowModal("create"),
				icon: <PlaylistAddIcon />,
			},
		];

		if (data) {
			options.unshift({
				name: "Edit Work Order",
				key: "edit_work_order",
				onClick: () => {
					setSelectedRowData(data);
					setShowModal("update");
				},
				icon: <EditIcon />,
				style: { fontWeight: "bold" },
			});

			if (data.Status === "Running" || data.Status === "Completed") {
				options.push({
					name: "Edit Times",
					key: "edit_times",
					onClick: () => {
						setSelectedRowData(data);
						setShowModal("times");
					},
					icon: <AccessTimeIcon />,
				});
			}

			if (data.Status === "Pending") {
				options.push({
					name: "Delete Work Order",
					key: "delete_work_order",
					onClick: () => {
						setSelectedRowData(data);
						setShowModal("delete");
					},
					icon: <DeleteIcon />,
				});
			}
		}

		return options;
	}, []);

	return { columnDefs, getMenuOptions };
};
